/* eslint-disable @typescript-eslint/naming-convention */
import React, {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState,
} from "react";
import { googleAuthConfig } from "./googleConfig";
import appConfig from "../../config";

/**
 * Google sign-in, done as a full-page redirect with the token exchange on the
 * server. Shaped like react-oidc-context's useAuth so the ~22 `useAuth()` call
 * sites need no changes.
 *
 * WHY NOT oidc-client-ts, LIKE ENTRA
 * ----------------------------------
 * Entra offers a `single_page_application` app registration: a public client
 * whose token endpoint takes authorization-code + PKCE with no secret and
 * serves CORS, so the library completes the flow in the browser. Google has no
 * equivalent for web. Its "Web application" client is confidential and its
 * token endpoint requires `client_secret` even with PKCE.
 *
 * That was established by running it: the redirect reached
 * `/callback?...&code=...&hd=cycraft.com` correctly and the backend saw
 * nothing, because the failure is entirely between the browser and
 * accounts.google.com (2026-08-08).
 *
 * WHY NOT GIS, WHICH IS WHAT SHIPPED FIRST
 * ----------------------------------------
 * Google Identity Services hands the browser an ID token with no token
 * endpoint and no secret, which solves the exchange — and it worked. What it
 * cannot do is renew. GIS issues one ID token, forgets you, and offers no
 * refresh, so the session ended after an hour with no warning. It also had to
 * use a popup, because GIS's redirect mode POSTs the credential to a server
 * endpoint that did not exist.
 *
 * WHAT THIS DOES INSTEAD
 * ----------------------
 *   1. `signinRedirect()` builds an authorize URL with PKCE + state and leaves
 *      the page. No popup, no COOP warnings, no third-party-cookie exposure.
 *   2. Google returns to `/callback?code=...`. This provider notices the code
 *      on mount — wherever the app happens to be mounted — and POSTs it to the
 *      BFF, which holds the client secret.
 *   3. The BFF returns the ID token and sets an httpOnly session cookie. The
 *      refresh token stays on the server; script can never read it.
 *   4. A timer renews the ID token before it expires, via that cookie.
 *
 * `access_token` still carries the ID token, as it did under GIS: every call
 * site feeds it to authHeaders() → `Authorization: Bearer …`, which is what M2
 * verifies. Renaming the field across ~30 call sites would buy nothing.
 */

const STORAGE_KEY = "xecguard:google-id-token";
const PKCE_KEY = "xecguard:google-pkce";
const STATE_KEY = "xecguard:google-state";

/**
 * Positive evidence that this browser has produced a renewable session before.
 * Its ABSENCE is what makes the next sign-in ask Google for consent.
 *
 * The flag is this way round — proof of success rather than proof of failure —
 * because of how `access_type=offline` actually behaves. It is necessary but
 * not sufficient: Google issues a refresh token only on the FIRST authorization
 * for a (user, client) pair. Every account that had already used this client
 * gets `access_type=offline` honoured and no refresh token, silently, because
 * from Google's side the grant already exists. On dev1 that was every account,
 * left over from the earlier oidc-client-ts and GIS attempts — confirmed in the
 * logs on 2026-08-08:
 *
 *   No refresh token for sub=…: Google returned none and none is stored.
 *
 * A "needs consent" flag that starts unset would take two sign-ins to heal:
 * one to discover the session is not renewable, another to act on it. Starting
 * from "not proven yet" heals on the first, and costs one consent screen per
 * browser rather than one per login.
 *
 * `prompt=consent` is what forces a fresh refresh token out of an existing
 * grant. It is not sent unconditionally because that re-prompts every time.
 *
 * localStorage, not sessionStorage: the evidence must outlive the browser
 * closing, or every cold start pays the consent screen again.
 */
const RENEWABLE_PROVEN_KEY = "xecguard:google-renewable-proven";

const AUTHORIZE_ENDPOINT = "https://accounts.google.com/o/oauth2/v2/auth";
const AUTH_BASE = "/xecguard/internal/v1/auth/google";

/** Renew this long before `exp`. Comfortably longer than any request. */
const RENEW_SKEW_SECONDS = 300;

export interface GoogleProfile {
  email?: string;
  name?: string;
  picture?: string;
  sub?: string;
  hd?: string;
  preferred_username?: string;
  exp?: number;
}

export interface GoogleUser {
  id_token: string;
  access_token: string; // same value; see the note above
  profile: GoogleProfile;
}

export interface GoogleAuthState {
  user: GoogleUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error?: { message: string };
  removeUser: () => Promise<void>;
  signinRedirect: (args?: unknown) => Promise<void>;
}

const decodeJwtPayload = (jwt: string): GoogleProfile => {
  try {
    const [, payload] = jwt.split(".");
    const json = atob(payload.replace(/-/g, "+").replace(/_/g, "/"));
    return JSON.parse(decodeURIComponent(escape(json))) as GoogleProfile;
  } catch {
    return {};
  }
};

const isExpired = (profile: GoogleProfile): boolean => {
  if (!profile.exp) return true;
  return profile.exp * 1000 <= Date.now() + 30_000;
};

// --- PKCE -------------------------------------------------------------------
// Still used even though the BFF's client secret would satisfy Google on its
// own. The secret proves the request came from this deployment; the verifier
// proves it came from the same browser that started the flow. Dropping PKCE
// because a secret exists would trade a real protection for nothing.

const base64url = (bytes: Uint8Array): string => {
  let binary = "";
  bytes.forEach((b) => { binary += String.fromCharCode(b); });
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
};

const randomString = (): string => base64url(crypto.getRandomValues(new Uint8Array(32)));

const needsConsent = (): boolean => {
  try {
    return window.localStorage.getItem(RENEWABLE_PROVEN_KEY) !== "1";
  } catch {
    // Storage unavailable: ask for consent. A redundant consent screen is a
    // minor annoyance; a session that cannot renew is the bug this fixes.
    return true;
  }
};

const challengeFor = async (verifier: string): Promise<string> => {
  const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(verifier));
  return base64url(new Uint8Array(digest));
};

// --- BFF calls --------------------------------------------------------------
// Raw fetch, NOT the shared axios instance. That instance has a 401 interceptor
// which redirects to /login — during a refresh, a 401 means "your session is
// over", and letting the interceptor see it would turn the renew path into a
// redirect loop. These three calls own their own error handling.

const authUrl = (path: string): string => `${appConfig.apiBaseUrl}${AUTH_BASE}${path}`;

interface TokenResponse {
  id_token: string;
  expires_at: number;
  email: string;
  renewable: boolean;
}

const postAuth = async (path: string, body?: unknown): Promise<TokenResponse | null> => {
  const response = await fetch(authUrl(path), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    // The session cookie is httpOnly and same-origin (the ALB serves the SPA
    // and the API from one host). "include" rather than "same-origin" so a
    // future split of the two hosts fails visibly at CORS instead of silently
    // dropping the cookie and looking like an expired session.
    credentials: "include",
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  if (!response.ok) return null;
  return (await response.json()) as TokenResponse;
};

const GoogleAuthContext = createContext<GoogleAuthState | null>(null);

export const GoogleAuthProvider = (
  { children }: { children: React.ReactNode },
): JSX.Element => {
  const [user, setUser] = useState<GoogleUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<{ message: string } | undefined>();
  const initialised = useRef(false);
  const renewTimer = useRef<number | undefined>(undefined);
  const renewable = useRef(false);

  const adopt = useCallback((tokens: TokenResponse) => {
    const profile = decodeJwtPayload(tokens.id_token);
    // The backend asserts `hd` too. Checking here as well only avoids adopting
    // a token we already know it will reject, so the user sees a message
    // instead of a silent 401 on every subsequent call.
    const wanted = googleAuthConfig.extraQueryParams?.hd;
    if (wanted && profile.hd !== wanted) {
      setError({ message: `Account is not in ${wanted}.` });
      setUser(null);
      return;
    }
    try { window.sessionStorage.setItem(STORAGE_KEY, tokens.id_token); } catch { /* ignore */ }
    // Self-healing: remember whether this session can renew, so the next
    // sign-in knows whether to ask Google for consent. See RENEWABLE_PROVEN_KEY.
    try {
      if (tokens.renewable) window.localStorage.setItem(RENEWABLE_PROVEN_KEY, "1");
      else window.localStorage.removeItem(RENEWABLE_PROVEN_KEY);
    } catch { /* ignore */ }
    renewable.current = tokens.renewable;
    setUser({ id_token: tokens.id_token, access_token: tokens.id_token, profile });
    setError(undefined);
  }, []);

  const clearLocal = useCallback(() => {
    try {
      window.sessionStorage.removeItem(STORAGE_KEY);
      window.sessionStorage.removeItem(PKCE_KEY);
      window.sessionStorage.removeItem(STATE_KEY);
    } catch { /* ignore */ }
    if (renewTimer.current) window.clearTimeout(renewTimer.current);
    renewTimer.current = undefined;
    setUser(null);
  }, []);

  // --- silent renew ---------------------------------------------------------
  // The reason this whole BFF exists. Reschedules itself after every success,
  // so a session lasts until the server-side session TTL rather than one hour.
  const scheduleRenew = useCallback((expSeconds: number) => {
    if (renewTimer.current) window.clearTimeout(renewTimer.current);
    if (!renewable.current) return; // honest: nothing to renew with

    const delayMs = Math.max(
      // Never busy-loop if a clock skew puts exp in the past — 10s is enough
      // to make progress and slow enough not to hammer the endpoint.
      10_000,
      expSeconds * 1000 - Date.now() - RENEW_SKEW_SECONDS * 1000,
    );
    renewTimer.current = window.setTimeout(async () => {
      const tokens = await postAuth("/refresh");
      if (tokens) {
        adopt(tokens);
        scheduleRenew(tokens.expires_at);
      } else {
        // The server has already cleared the cookie. Drop the local token so
        // the app renders /login instead of retrying a dead session, and mark
        // the next sign-in as needing consent — a refresh that fails means the
        // grant is gone or was never issued, which is exactly what consent
        // fixes.
        try { window.localStorage.removeItem(RENEWABLE_PROVEN_KEY); } catch { /* ignore */ }
        clearLocal();
      }
    }, delayMs) as unknown as number;
  }, [adopt, clearLocal]);

  useEffect(() => {
    if (initialised.current) return;
    initialised.current = true;

    const boot = async (): Promise<void> => {
      const params = new URLSearchParams(window.location.search);
      const code = params.get("code");
      const returnedState = params.get("state");
      const oauthError = params.get("error");

      if (oauthError) {
        // The user pressed Cancel, or the client is misconfigured. Either way
        // the code path below has nothing to work with.
        setError({ message: `Google returned: ${oauthError}` });
        setIsLoading(false);
        return;
      }

      if (code) {
        const expectedState = window.sessionStorage.getItem(STATE_KEY) || "";
        const verifier = window.sessionStorage.getItem(PKCE_KEY) || "";
        // Constant-ish comparison is pointless here (the value is in this same
        // browser's storage), but the check itself is not: without it, an
        // attacker can feed the app a code from a session they control.
        if (!expectedState || returnedState !== expectedState || !verifier) {
          setError({ message: "Sign-in state did not match. Please try again." });
          setIsLoading(false);
          return;
        }

        const tokens = await postAuth("/exchange", { code, code_verifier: verifier });

        // Strip code/state from the address bar. An authorization code in
        // history is a credential in history, even a spent one.
        //
        // Rewrites the QUERY only, staying on the current path. Rewriting it
        // to /dashboard would put the address bar and React Router out of
        // step — replaceState fires no popstate, so the router would still
        // believe it is on /callback while the URL claimed otherwise. Getting
        // to /dashboard is Callback.tsx's job, through a real router
        // navigation, once isAuthenticated flips below.
        window.sessionStorage.removeItem(PKCE_KEY);
        window.sessionStorage.removeItem(STATE_KEY);
        window.history.replaceState({}, "", window.location.pathname);

        if (!tokens) {
          setError({ message: "Sign-in failed. Please try again." });
          setIsLoading(false);
          return;
        }
        adopt(tokens);
        scheduleRenew(tokens.expires_at);
        setIsLoading(false);
        return;
      }

      // No code in the URL: an ordinary page load. Restore a live token if we
      // have one, and otherwise try the cookie — which is what makes a browser
      // restart resume the session instead of bouncing to /login.
      let restored: GoogleUser | null = null;
      try {
        const stored = window.sessionStorage.getItem(STORAGE_KEY);
        if (stored) {
          const profile = decodeJwtPayload(stored);
          if (!isExpired(profile)) {
            restored = { id_token: stored, access_token: stored, profile };
            setUser(restored);
            renewable.current = true; // assume; a failed refresh corrects it
            scheduleRenew(profile.exp || 0);
          } else {
            window.sessionStorage.removeItem(STORAGE_KEY);
          }
        }
      } catch { /* ignore */ }

      if (!restored) {
        const tokens = await postAuth("/refresh");
        if (tokens) {
          adopt(tokens);
          scheduleRenew(tokens.expires_at);
        }
      }
      setIsLoading(false);
    };

    boot().catch((e: Error) => {
      setError({ message: e.message });
      setIsLoading(false);
    });
  }, [adopt, scheduleRenew]);

  const removeUser = useCallback(async () => {
    // Ends the session HERE only. Google's own session is deliberately left
    // alone: accounts.google.com/Logout would sign the user out of Gmail,
    // Calendar and everything else in the browser, and Google offers no
    // app-scoped equivalent of Entra's logout endpoint.
    try { await postAuth("/logout"); } catch { /* sign-out must not fail */ }
    clearLocal();
  }, [clearLocal]);

  const signinRedirect = useCallback(async () => {
    const verifier = randomString();
    const state = randomString();
    const challenge = await challengeFor(verifier);

    try {
      window.sessionStorage.setItem(PKCE_KEY, verifier);
      window.sessionStorage.setItem(STATE_KEY, state);
    } catch {
      setError({ message: "Session storage is unavailable; sign-in cannot proceed." });
      return;
    }

    const params = new URLSearchParams({
      client_id: googleAuthConfig.client_id,
      redirect_uri: googleAuthConfig.redirect_uri,
      response_type: "code",
      scope: googleAuthConfig.scope,
      state,
      code_challenge: challenge,
      code_challenge_method: "S256",
      // Without this Google returns no refresh token and the BFF has nothing
      // to renew with — which would leave us exactly where GIS was.
      access_type: "offline",
      // `consent` ONLY when the last sign-in came back un-renewable. Sending
      // it always would re-prompt on every login; never sending it leaves
      // every pre-existing account permanently unable to renew, because their
      // grant predates access_type=offline. See RENEWABLE_PROVEN_KEY.
      prompt: needsConsent() ? "select_account consent" : "select_account",
    });
    const hd = googleAuthConfig.extraQueryParams?.hd;
    if (hd) params.set("hd", hd);

    window.location.assign(`${AUTHORIZE_ENDPOINT}?${params.toString()}`);
  }, []);

  const value = useMemo<GoogleAuthState>(() => ({
    user,
    isAuthenticated: !!user,
    isLoading,
    error,
    removeUser,
    signinRedirect,
  }), [user, isLoading, error, removeUser, signinRedirect]);

  return (
    <GoogleAuthContext.Provider value={value}>{children}</GoogleAuthContext.Provider>
  );
};

export const useGoogleAuth = (): GoogleAuthState => {
  const ctx = useContext(GoogleAuthContext);
  if (!ctx) throw new Error("useGoogleAuth must be used inside <GoogleAuthProvider>");
  return ctx;
};
